import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Rocket, Send, Building, Target, Zap } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwy18P6DysHccMoQiOifyvrBkdTsPB_oQYYRbuutFFqZJd1spR0kIrFVVsZ0ZNIw_qOUw/exec";

const StartupRegistrationModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    startupName: "",
    industry: "",
    stage: "",
    message: "",
  });

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        formType: "Startup Registration",
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.startupName,
        position: `${formData.industry} - ${formData.stage}`,
        cover_letter: formData.message,
      };

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: "Startup Registered!",
          description:
            "Welcome to Maroons. Our incubation team will contact you shortly.",
          className: "bg-green-50 border-green-200 text-green-900",
        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          startupName: "",
          industry: "",
          stage: "",
          message: "",
        });
        onClose();
      } else {
        throw new Error("Network response was not ok");
      }
    } catch (error) {
      toast({
        title: "Registration Failed",
        description:
          "There was a problem submitting your application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Now with padding on all screens so it never touches edges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          >
            {/* Modal Container - Floating centered modal with rounded corners everywhere */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5, bounce: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.4)] overflow-hidden flex flex-col md:flex-row"
            >
              {/* Universal Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 md:top-6 md:right-6 z-50 p-2 md:p-2.5 bg-black/20 hover:bg-black/40 md:bg-slate-100/80 md:hover:bg-slate-200 backdrop-blur-md text-white md:text-slate-600 rounded-full transition-all shadow-sm"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* --- MOBILE PREMIUM HEADER --- */}
              <div className="md:hidden relative bg-gradient-to-br from-[#8C1C1C] to-[#4a0e0e] px-6 pt-8 pb-10 flex-shrink-0">
                {/* Mobile Background Orbs */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-[40px] -translate-y-1/4 translate-x-1/4"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/30 rounded-full blur-[30px] translate-y-1/2 -translate-x-1/4"></div>

                <div className="relative z-10">
                  {/* Significantly increased logo size for mobile */}
                  <img
                    src="/logo-horizontal.png"
                    alt="Maroons Logo"
                    className="h-20 sm:h-22 w-auto object-contain brightness-0 invert mb-6 drop-shadow-md"
                  />

                  <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-4 backdrop-blur-sm shadow-inner">
                    <Rocket className="w-3 h-3 text-[#ffcaca] mr-1.5" />
                    <span className="text-[10px] font-bold text-white tracking-widest uppercase">
                      Incubate
                    </span>
                  </div>
                  <h2 className="text-3xl font-black text-white leading-tight drop-shadow-sm">
                    Scale Your <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-[#ffcaca]">
                      Vision.
                    </span>
                  </h2>
                </div>
              </div>

              {/* --- DESKTOP PREMIUM BRANDING PANEL --- */}
              <div className="hidden md:flex md:w-5/12 relative bg-gradient-to-br from-[#8C1C1C] to-[#4a0e0e] p-12 flex-col justify-between overflow-hidden">
                {/* Desktop Decorative Background Elements */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-black/30 rounded-full blur-[60px] translate-y-1/3 -translate-x-1/4"></div>

                <div className="relative z-10">
                  <img
                    src="/logo-horizontal.png"
                    alt="Maroons Logo"
                    className="h-14 w-auto object-contain brightness-0 invert mb-6 drop-shadow-md"
                  />

                  <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 border border-white/20 mb-6 backdrop-blur-sm shadow-inner">
                    <Rocket className="w-4 h-4 text-[#ffcaca] mr-2" />
                    <span className="text-xs font-bold text-white tracking-widest uppercase">
                      Maroons Startup Program
                    </span>
                  </div>

                  <h2 className="text-4xl lg:text-5xl font-black text-white leading-tight mb-6 drop-shadow-sm">
                    Incubate Your <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-[#ffcaca]">
                      Vision.
                    </span>
                  </h2>
                  <p className="text-[#ffcaca] text-lg leading-relaxed font-medium">
                    Join an elite ecosystem designed to engineer resilient
                    foundations, scale operations, and drive measurable
                    commercial growth for emerging startups.
                  </p>
                </div>

                {/* Feature List */}
                <div className="relative z-10 space-y-6 mt-12">
                  <div className="flex items-start space-x-4 bg-white/5 p-4 rounded-2xl backdrop-blur-sm border border-white/10">
                    <div className="p-2.5 bg-[#8C1C1C]/50 rounded-xl shadow-inner">
                      <Target className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm">
                        Strategic Incubation
                      </h4>
                      <p className="text-[#ffcaca] text-xs mt-1 leading-relaxed">
                        End-to-end guidance from ideation to scale.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 bg-white/5 p-4 rounded-2xl backdrop-blur-sm border border-white/10">
                    <div className="p-2.5 bg-[#8C1C1C]/50 rounded-xl shadow-inner">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm">
                        Resource Allocation
                      </h4>
                      <p className="text-[#ffcaca] text-xs mt-1 leading-relaxed">
                        Access to premium tech, finance, and marketing infra.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* --- THE FORM AREA (Mobile & Desktop) --- */}
              <div className="w-full md:w-7/12 bg-white px-6 py-8 sm:p-10 lg:p-12 overflow-y-auto flex-1 custom-scrollbar">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-8 md:space-y-6"
                >
                  {/* Personal Details Section */}
                  <div className="space-y-5">
                    <h3 className="text-xs sm:text-sm font-black text-slate-800 uppercase tracking-widest border-b border-slate-100 pb-3 flex items-center">
                      <span className="w-2 h-2 rounded-full bg-[#8C1C1C] mr-2"></span>
                      Founder Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label
                          htmlFor="reg-name"
                          className="text-slate-700 font-bold text-xs"
                        >
                          Full Name *
                        </Label>
                        <Input
                          id="reg-name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Jane Doe"
                          className="bg-slate-50/50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl h-12 md:h-11 shadow-sm transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="reg-phone"
                          className="text-slate-700 font-bold text-xs"
                        >
                          Phone Number *
                        </Label>
                        <Input
                          id="reg-phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 XXXXX XXXXX"
                          className="bg-slate-50/50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl h-12 md:h-11 shadow-sm transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="reg-email"
                        className="text-slate-700 font-bold text-xs"
                      >
                        Email Address *
                      </Label>
                      <Input
                        id="reg-email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="founder@startup.com"
                        className="bg-slate-50/50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl h-12 md:h-11 shadow-sm transition-all"
                      />
                    </div>
                  </div>

                  {/* Startup Details Section */}
                  <div className="space-y-5 pt-2">
                    <h3 className="text-xs sm:text-sm font-black text-slate-800 uppercase tracking-widest border-b border-slate-100 pb-3 flex items-center">
                      <span className="w-2 h-2 rounded-full bg-slate-800 mr-2"></span>
                      Startup Profile
                    </h3>
                    <div className="space-y-2">
                      <Label
                        htmlFor="reg-startupName"
                        className="text-slate-700 font-bold text-xs"
                      >
                        Startup Name *
                      </Label>
                      <div className="relative">
                        <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="reg-startupName"
                          name="startupName"
                          required
                          value={formData.startupName}
                          onChange={handleChange}
                          placeholder="e.g. Acme Innovations"
                          className="bg-slate-50/50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] rounded-xl h-12 md:h-11 pl-11 shadow-sm transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label
                          htmlFor="reg-industry"
                          className="text-slate-700 font-bold text-xs"
                        >
                          Industry Sector *
                        </Label>
                        <select
                          id="reg-industry"
                          name="industry"
                          required
                          value={formData.industry}
                          onChange={handleChange}
                          className="w-full bg-slate-50/50 border border-slate-200 rounded-xl px-3 h-12 md:h-11 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#8C1C1C] focus:border-transparent shadow-sm transition-all"
                        >
                          <option value="">Select Industry</option>
                          <option value="SaaS / Software">
                            SaaS / Software
                          </option>
                          <option value="Fintech">Fintech</option>
                          <option value="Healthtech">Healthtech</option>
                          <option value="E-commerce / Retail">
                            E-commerce / Retail
                          </option>
                          <option value="Agritech">Agritech</option>
                          <option value="Edtech">Edtech</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="reg-stage"
                          className="text-slate-700 font-bold text-xs"
                        >
                          Current Stage *
                        </Label>
                        <select
                          id="reg-stage"
                          name="stage"
                          required
                          value={formData.stage}
                          onChange={handleChange}
                          className="w-full bg-slate-50/50 border border-slate-200 rounded-xl px-3 h-12 md:h-11 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#8C1C1C] focus:border-transparent shadow-sm transition-all"
                        >
                          <option value="">Select Stage</option>
                          <option value="Idea Phase">Idea Phase</option>
                          <option value="MVP / Prototype">
                            MVP / Prototype
                          </option>
                          <option value="Early Revenue">Early Revenue</option>
                          <option value="Scaling">Scaling / Growth</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="reg-message"
                        className="text-slate-700 font-bold text-xs"
                      >
                        Brief Pitch & What You Need Help With *
                      </Label>
                      <Textarea
                        id="reg-message"
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about the problem you are solving and how Maroons can accelerate your growth..."
                        className="bg-slate-50/50 border-slate-200 focus:border-[#8C1C1C] focus:ring-[#8C1C1C] resize-none rounded-xl p-4 md:p-3 text-sm shadow-sm transition-all"
                      />
                    </div>
                  </div>

                  <div className="pt-4 pb-2">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#8C1C1C] hover:bg-[#6c1616] text-white h-14 md:h-12 rounded-xl font-bold text-base shadow-[0_8px_20px_rgba(140,28,28,0.2)] transition-all active:scale-[0.98]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : (
                        <>
                          Apply for Incubation
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-center text-slate-400 font-medium mt-4">
                      Your data is secure. We'll review and respond within 48
                      hours.
                    </p>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StartupRegistrationModal;
